import * as ko from "knockout";
import PromiseRequest from "../../../../../req/PromiseRequest";
import {
    ICompanySyncstep,
    ICompanySyncstepParameter,
    IExportAttribute,
    IMessage,
    IMessageData,
    IObjectData,
    ISearchObjectParams,
} from "../../../../../index";
import {global} from "../../../../../appContext";
import {REQUEST_MESSAGE} from "../../../../../constants/Language";
import {credentialMatchesProfile, getRawSyncFieldValue} from "../../../../../util/misc";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../../../constants";
import {getSearchObjectParams, getSearchParams} from "../../../../../util/request";

interface IParams {
    companysyncstep: ICompanySyncstep
}

interface IClientCredentialProfile {
    integrationcode: string
}

export interface IClientCredentialOption {
    id: number
    name: string
    description: string
    integrationCode: string
    clientIdOrUsername: string
    expiryDate: string
    fingerprint: string
}

export interface IClientCredentialDropdownOption {
    idAsString: string
    label: string
}

export interface IMessageFlowDropdownOption {
    idAsString: string
    label: string
}

const CLIENT_CREDENTIAL_REF_KEY = 'client credential reference';

export class Abstract {
    protected data: ICompanySyncstep;
    protected readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected readonly req_getallcompanysyncstepparameters: PromiseRequest = global.getPromiseRequest('getallcompanysyncstepparameters');
    protected readonly req_getcompanysyncstepparameterkeyvalues: PromiseRequest = global.getPromiseRequest('getcompanysyncstepparameterkeyvalues');
    protected companySyncstepParameters!: ICompanySyncstepParameter[];
    protected availableSyncstepParameters!: IExportAttribute[];

    protected readonly reqGetClientCredentialProfile = new PromiseRequest('getclientcredentialprofile');
    protected readonly reqSearchClientCredentials = new PromiseRequest('searchobject');
    protected readonly reqSearchMessageFlows = new PromiseRequest('searchmessage');
    protected readonly clientCredentialIntegrationCode: KnockoutObservable<string> = ko.observable('');
    protected readonly clientCredentialOptions: KnockoutObservableArray<IClientCredentialOption> = ko.observableArray<IClientCredentialOption>([]);
    protected readonly messageFlowOptions: KnockoutObservableArray<IMessageFlowDropdownOption> = ko.observableArray<IMessageFlowDropdownOption>([]);
    protected readonly clientCredentialLabelById: KnockoutComputed<Record<string, string>> = ko.pureComputed(() => {
        const map: Record<string, string> = {};
        this.clientCredentialOptions().forEach((option) => {
            map[String(option.id)] = this.formatClientCredentialLabel(option);
        });
        return map;
    });

    constructor(params: IParams) {
        this.data = params.companysyncstep
    }

    protected init(): Promise<void> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        return Promise.resolve()
            .then(() => {
                return this.req_getallcompanysyncstepparameters
                    .request({
                        companysyncstepid: this.data.id,
                    })
                    .then((data: ICompanySyncstepParameter[]) => {
                        this.companySyncstepParameters = data
                    })
            })
            .then(() => {
                return this.req_getcompanysyncstepparameterkeyvalues
                    .request({
                        syncstepid: this.data.syncstep.id,
                    })
                    .then((data: IExportAttribute[]) => {
                        this.availableSyncstepParameters = data
                    })
            })
    }

    protected getFormatType(key: string): IExportAttribute['type'] | undefined {
        return this.availableSyncstepParameters.find(o => o.name == key)?.type
    }

    protected isClientCredentialRefKey(key: string): boolean {
        return key?.toLowerCase() === CLIENT_CREDENTIAL_REF_KEY;
    }

    protected hasClientCredentialRefSupport(): boolean {
        return this.availableSyncstepParameters?.some((p) => this.isClientCredentialRefKey(p.name)) ?? false;
    }

    protected loadClientCredentialContext(): Promise<void> {
        if (!this.hasClientCredentialRefSupport()) {
            this.clientCredentialIntegrationCode('');
            this.clientCredentialOptions([]);
            return Promise.resolve();
        }

        this.reqGetClientCredentialProfile.abort();
        return this.reqGetClientCredentialProfile
            .request({syncstepid: this.data.syncstep.id})
            .then((data: IClientCredentialProfile | IClientCredentialProfile[]) => {
                const profile = Array.isArray(data) ? data[0] : data;
                const integrationCode = profile?.integrationcode ?? '';
                this.clientCredentialIntegrationCode(integrationCode);
                return this.loadClientCredentialsForIntegration(integrationCode);
            });
    }

    protected loadMessageFlowContext(): Promise<void> {
        const supportsMessageRefs = this.availableSyncstepParameters?.some(
            parameter => parameter.type.toLowerCase() === 'messagerefs'
        ) ?? false;
        if (!supportsMessageRefs) {
            this.messageFlowOptions([]);
            return Promise.resolve();
        }

        this.reqSearchMessageFlows.abort();
        return this.reqSearchMessageFlows
            .request(getSearchParams({
                resultsprpage: MAX_RESULTS_PER_PAGE,
                sortcol: 'name',
            }))
            .then((messages: IMessage[]) => {
                this.messageFlowOptions(
                    messages
                        .filter(message => message.messagetypeid < 6 || message.messagetypeid > 9)
                        .map(message => ({
                            idAsString: String(message.id),
                            label: `${message.name} (#${message.id})${message.active ? '' : ' — inactive'}`,
                        }))
                );
            });
    }

    private loadClientCredentialsForIntegration(integrationCode: string): Promise<void> {
        this.reqSearchClientCredentials.abort();
        const params: ISearchObjectParams = getSearchObjectParams({
            objecttypeid: ObjectTypeId.ClientCredential,
            manualobjects: true,
            searchcols: 'Name,Description,ExtensionAttribute1,ExtensionAttribute5,ExtensionAttribute4,ExpiryDate',
            sortcol: 'Name',
            deleted: 0,
            resultsprpage: 1000,
            pagenr: 0,
        });

        return this.reqSearchClientCredentials
            .request(params)
            .then((data: IObjectData[]) => {
                const all: IClientCredentialOption[] = data.map((obj) => ({
                    id: obj.id,
                    name: getRawSyncFieldValue('Name', obj.syncfields) ?? '',
                    description: getRawSyncFieldValue('Description', obj.syncfields) ?? '',
                    clientIdOrUsername: getRawSyncFieldValue('ExtensionAttribute1', obj.syncfields) ?? '',
                    integrationCode: getRawSyncFieldValue('ExtensionAttribute5', obj.syncfields) ?? '',
                    expiryDate: getRawSyncFieldValue('ExpiryDate', obj.syncfields) ?? '',
                    fingerprint: getRawSyncFieldValue('ExtensionAttribute4', obj.syncfields) ?? '',
                }));
                const filtered = all.filter((o) => credentialMatchesProfile(o.integrationCode, integrationCode));
                this.clientCredentialOptions(filtered);
            });
    }

    protected getClientCredentialDropdownOptions(): IClientCredentialDropdownOption[] {
        return this.clientCredentialOptions().map((option) => ({
            idAsString: String(option.id),
            label: this.formatClientCredentialLabel(option),
        }));
    }

    protected getClientCredentialOptionById(selectedId: string | number): IClientCredentialOption {
        return this.clientCredentialOptions().find((option) => String(option.id) === String(selectedId))!;
    }

    private formatClientCredentialLabel(option: IClientCredentialOption): string {
        const trimmedName = option.name?.trim() || `#${option.id}`;
        const trimmedFingerprint = option.fingerprint?.trim();
        return trimmedFingerprint ? `${trimmedName} (${trimmedFingerprint})` : trimmedName;
    }

    dispose() {
        this.req_getallcompanysyncstepparameters.abort()
        this.req_getcompanysyncstepparameterkeyvalues.abort()
        this.reqGetClientCredentialProfile.abort()
        this.reqSearchClientCredentials.abort()
        this.reqSearchMessageFlows.abort()
        this.clientCredentialLabelById.dispose()
    }
}
